import { BlockHrefTargetEnum, BlockHrefTypeEnum, BlockNodeEnum } from '@core/enums';
import { downloadAndSaveFileWithProgress } from '@shared/helpers/download-file';

const INTERNAL_HREF_TYPES = [
  BlockHrefTypeEnum.INTERNAL_URL_HREF_TYPE,
  BlockHrefTypeEnum.OBJECT_HREF_TYPE_SELF,
  BlockHrefTypeEnum.OBJECT_HREF_TYPE_ARTIST,
  BlockHrefTypeEnum.OBJECT_HREF_TYPE_EXHIBITION,
  BlockHrefTypeEnum.INTERNAL_URL_HREF_TYPE_PARENT,
];

export class BlockLink {
  hrefType: BlockHrefTypeEnum | null = null;
  hrefTarget: BlockHrefTargetEnum | null = null;
  hrefUrl: string | null = null;
  hrefNoFollow = false;
  hrefAnchor: string | null;
  hrefQueryParams: Record<string, string> | null;
  locale: string;
  blockNode: BlockNodeEnum;
  linkDescription: any;
  hrefDownload: boolean;
  hrefDownloadName: string | null = null;

  constructor(blockData: any) {
    Object.assign(this, {
      locale: blockData.locale,
      hrefType: blockData.hrefType,
      hrefTarget: blockData.hrefTarget,
      hrefUrl: blockData.hrefUrl,
      hrefAnchor: blockData.hrefAnchor,
      hrefNoFollow: blockData.hrefNoFollow,
      hrefQueryParams: blockData.hrefQueryParams,
      blockNode: blockData.node,
      linkDescription: blockData.linkDescription,
      hrefDownload: blockData.hrefDownload,
      hrefDownloadName: blockData.hrefDownloadName,
    });

  }

  getLink() {
    const {hrefUrl, locale} = this;
    let link = hrefUrl;
    if (hrefUrl) {
      if (
        this.hasExternalLink() &&
        !this.isEmail() &&
        !hrefUrl.startsWith('http://') &&
        !hrefUrl.startsWith('https://')
      ) {
        link = `http://${locale}/${hrefUrl}`;
      } else if (this.isEmail()) {
        link = hrefUrl;
      } else {
        if (this.blockNode === BlockNodeEnum.SNIPPET_NODE && this.getLocaleForURL(hrefUrl)) {
          link = `/${locale}${hrefUrl}`;
        }
      }

      return link;
    }
  }

  getDownload(): string | null {
    return this.hrefDownload || this.hrefType === BlockHrefTypeEnum.FILE_HREF_TYPE
      ? (this.hrefDownloadName || this.getLink()?.split('/').pop())
      : null;
  }

  onClickLink(ev: MouseEvent) {
    if (!this.hasLink()) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
    const link = this.getLink();
    const filename = (this.hrefDownloadName || link.split('/').pop());

    if (this.hrefDownload || this.hrefType === BlockHrefTypeEnum.FILE_HREF_TYPE) {
      downloadAndSaveFileWithProgress({
        url: link,
        filename: filename,
      });
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
  }

  getQueryParams() {
    return {
      ...(this.hrefQueryParams || {}),
    };
  }

  hasInternalLink() {
    const {hrefType} = this;
    return (
      this.hasLink() &&
      INTERNAL_HREF_TYPES.includes(hrefType)
    );
  }

  hasExternalLink() {
    const {hrefType} = this;
    return (
      this.hasLink() &&
      !INTERNAL_HREF_TYPES.includes(hrefType)
    );
  }

  getRel(): string {
    return this.hrefNoFollow ? 'nofollow' : '';
  }

  getTarget() {
    return this.isTargetBlank()
      ? '_blank'
      : '_self';
  }

  private isTargetBlank() {
    return this.hrefTarget === BlockHrefTargetEnum.BLANK_HREF_TARGET || this.hrefTarget === BlockHrefTargetEnum.BLANK_HREF_TARGET_STRING;
  }

  hasLink(): boolean {
    const {hrefType, hrefTarget, hrefUrl} = this;
    return !(
      hrefType === null || hrefType === undefined ||
      hrefTarget === null || hrefTarget === undefined ||
      hrefUrl === null || hrefUrl === undefined ||
      hrefUrl === ''
    );
  }

  private isEmail() {
    return this.hrefType === BlockHrefTypeEnum.EMAIL_HREF_TYPE;
  }

  private getLocaleForURL(url: string) {
    const arr = url.split('/');
    // if any part of url is lang code then return false
    return !arr.some(e => ['pl', 'en', 'de', 'es', 'fr', 'ru'].includes(e));
  }

}
